// src/services/websocket/websocketService.ts
import { webSocketConfig } from '../../config/websocket'; // Assuming config exists
import { errorManager, ErrorSeverity } from '../error/ErrorManager'; // Import ErrorManager
import { mythbound } from '../../generated/messages';
import { validateServerMessage } from './messageValidator';
import { buildAuthHeaders } from '../api/authTransport';

export type RealtimeConnectionStatus = 'connecting' | 'authenticating' | 'synchronizing' | 'ready' | 'retrying' | 'reconnect_exhausted' | 'unavailable';
export interface RealtimeEnvelope { type: 'state.snapshot' | 'state.changed'; protocol_version: '1.0.0'; event_id: string; campaign_id: string; session_id: string | null; server_revision: number; request_id?: string; data: Record<string, unknown>; }

type HandshakeMessage = {
  protocol_version?: string;
  contract_versions?: Record<string, string>;
  serializers?: {
    preferred?: string;
    supported?: string[];
  };
  session?: {
    user_id?: string;
    connection_id?: string;
    role?: string;
    platform_type?: string;
  };
};

/**
 * Owns the campaign WebSocket lifecycle and snapshot synchronization boundary.
 * Server revisions and validated snapshots establish state authority; legacy
 * sequenceId values are correlation labels only, never ordering or replay authority.
 * The frozen campaign route is read-only apart from refresh and ping controls.
 */
export class WebSocketService {
  private socket: WebSocket | null = null;
  private eventHandlers: Map<string, Set<(data: any) => void>> = new Map();
  private reconnectAttempts: number = 0;
  private readonly maxReconnectAttempts: number = webSocketConfig.maxReconnectAttempts || 5;
  private readonly reconnectDelay: number = webSocketConfig.reconnectDelay || 1000;
  private readonly reconnectBackoffFactor: number = webSocketConfig.reconnectBackoffFactor || 1.5;
  private isConnecting: boolean = false;
  private explicitlyClosed: boolean = false;
  private handshakeValidated: boolean = false;
  private handshakeConfirmed: boolean = false;
  private lastHandshake: HandshakeMessage | null = null;
  private readonly expectedProtocolVersion = '1.0.0';
  private readonly expectedContractVersions = {
    websocket: '1.0.0',
    protobuf_schema: '1.0.0',
  };
  private readonly preferredSerializer = 'json';
  private readonly clientIdentifier = webSocketConfig.clientIdentifier || 'webapp';
  private readonly clientVersion = webSocketConfig.clientVersion || 'dev';
  // Fences callbacks and async auth-header results from a previous socket,
  // especially when disconnect happens before the lookup resolves.
  private connectionGeneration = 0;
  private campaignId: string | null = null;
  private connectionStatus: RealtimeConnectionStatus = 'unavailable';
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private refreshRequestId = 0;
  private lifecycleTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly preHandshakeAllowed = new Set([
    'handshake.ack',
    'handshake.error',
  ]);

  /**
   * Creates an instance of WebSocketService.
   */
  constructor() {
    // Scope is supplied by the campaign route before any authenticated handshake.
    console.log('WebSocketService instance created. Waiting for campaign scope.');
  }

  /**
   * Establishes a WebSocket connection to the backend.
   */
  /**
   * Opens transport only. The service remains not-ready until this socket
   * receives a version-valid acknowledgement from the server.
   */
  connect(campaignId?: string) {
    if (campaignId) this.campaignId = campaignId;
    if (!this.campaignId) { this.setConnectionStatus('unavailable'); return; }
    if (this.socket?.readyState === WebSocket.OPEN || this.isConnecting) {
      return;
    }

    this.explicitlyClosed = false;
    this.isConnecting = true;
    if (this.lifecycleTimer) clearTimeout(this.lifecycleTimer);
    this.setConnectionStatus(this.reconnectAttempts ? 'retrying' : 'connecting');
    this.handshakeValidated = false;
    this.handshakeConfirmed = false;
    this.lastHandshake = null;
    const generation = ++this.connectionGeneration;
    this.lifecycleTimer = setTimeout(() => { if (this.connectionGeneration === generation && this.socket && !this.handshakeValidated) this.socket.close(4002, 'Realtime handshake timed out'); }, 10000);

    try {
      const socket = new WebSocket(this.getCampaignEndpoint(this.campaignId));
      this.socket = socket;
      socket.onopen = () => this.handleOpen(socket, generation);
      socket.onclose = (event) => this.handleClose(socket, generation, event);
      socket.onerror = (event) => this.handleError(socket, generation, event);
      socket.onmessage = (event: MessageEvent) => {
        if (!this.isCurrentConnection(socket, generation)) return;
        if (event.data instanceof ArrayBuffer || event.data instanceof Blob) {
          if (!this.handshakeValidated) {
            errorManager.reportError(
              'Ignored binary WebSocket data before server authentication.',
              ErrorSeverity.WARNING,
              'WebSocketService.onmessage',
              {}
            );
            return;
          }
          void this.handleBinaryMessage(event, socket, generation);
        } else {
          this.handleMessage(event);
        }
      };
    } catch {
      errorManager.reportError(
        'WebSocket initial connection failed.',
        ErrorSeverity.ERROR,
        'WebSocketService.connect',
        {}
      );
      this.isConnecting = false;
      this.attemptReconnect();
    }
  }

  /**
   * Closes the WebSocket connection explicitly.
   */
  disconnect() {
    this.explicitlyClosed = true;
    this.reconnectAttempts = 0;
    this.connectionGeneration += 1;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    if (this.lifecycleTimer) clearTimeout(this.lifecycleTimer);
    this.lifecycleTimer = null;
    this.campaignId = null;
    this.handshakeValidated = false;
    this.handshakeConfirmed = false;
    this.lastHandshake = null;
    const socket = this.socket;
    this.socket = null;
    this.isConnecting = false;
    if (socket) {
      socket.close(1000, 'Client initiated disconnect');
      this.dispatchLocalEvent('connection_closed', { code: 1000 });
    }
    this.setConnectionStatus('unavailable');
  }


  /** Campaign endpoints preserve a configured deployment prefix and drop credentials/query data. */
  private getCampaignEndpoint(campaignId: string): string {
    const configured = new URL(webSocketConfig.endpoint, window.location.href);
    const routePrefix = configured.pathname.replace(/\/ws(?:\/(?:self|campaigns\/[^/]+|[^/]+))?\/?$/, '').replace(/\/$/, '');
    configured.pathname = `${routePrefix}/ws/campaigns/${encodeURIComponent(campaignId)}`;
    configured.search = ''; configured.hash = '';
    return configured.toString();
  }

  private setConnectionStatus(status: RealtimeConnectionStatus) {
    if (status === this.connectionStatus) return;
    this.connectionStatus = status;
    this.dispatchLocalEvent('connection_status', { status });
  }

  getConnectionStatus(): RealtimeConnectionStatus { return this.connectionStatus; }

  retry() {
    const campaignId = this.campaignId;
    if (!campaignId) return;
    this.disconnect(); this.campaignId = campaignId; this.reconnectAttempts = 0; this.explicitlyClosed = false; this.connect(campaignId);
  }

  /**
   * Legacy voice tests and older callers still invoke this setter. Campaign rooms
   * derive gameplay session identity from validated snapshots, so caller-provided
   * IDs are intentionally ignored and never serialized into transport messages.
   */
  setSessionId(_sessionId: string): void { /* campaign scope is not a gameplay session ID */ }

  requestStateRefresh(): string | null {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN || !this.handshakeValidated || !this.campaignId) return null;
    const targetSocket = this.socket;
    const generation = this.connectionGeneration;
    const requestId = `refresh-${Date.now()}-${++this.refreshRequestId}`;
    try { targetSocket.send(JSON.stringify({ type: 'state.refresh', data: { request_id: requestId } })); this.setConnectionStatus('synchronizing'); if (this.lifecycleTimer) clearTimeout(this.lifecycleTimer); this.lifecycleTimer = setTimeout(() => { if (this.connectionGeneration === generation && this.socket === targetSocket && this.handshakeValidated && this.connectionStatus === 'synchronizing') { this.setConnectionStatus('unavailable'); targetSocket.close(4003, 'Realtime snapshot timed out'); } }, 10000); return requestId; }
    catch { return null; }
  }

  markSynchronized() { if (this.handshakeValidated) { if (this.lifecycleTimer) clearTimeout(this.lifecycleTimer); this.lifecycleTimer = null; this.reconnectAttempts = 0; this.setConnectionStatus('ready'); } }

  /**
   * Sends an allowed JSON control message; legacy sequenceId is only a correlation label.
   * @param {string} eventType - The type of the event being sent.
   * @param {any} data - The payload data for the event.
   * @returns {boolean} - True if the message was sent successfully, false otherwise.
   */
  send(eventType: string, data: any): boolean {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      this.dispatchLocalEvent('send_failed', { eventType, reason: 'offline' });
      errorManager.reportError(
        'Cannot send message: WebSocket not connected or not open.',
        ErrorSeverity.WARNING, // Warning, as this might be expected if offline
        'WebSocketService.send',
        { eventType, readyState: this.socket?.readyState }
      );
      // Keep unsent messages with the caller; this transport never queues or replays them.
      return false;
    }

    if (this.connectionStatus !== 'ready') {
      this.dispatchLocalEvent('send_failed', { eventType, reason: 'not_ready' });
      errorManager.reportError('Cannot send message before campaign snapshot synchronization.', ErrorSeverity.WARNING, 'WebSocketService.send', { eventType });
      return false;
    }

    // The frozen campaign route accepts refresh/ping controls only. Returning false here keeps
    // unsupported state-changing sends visibly unsent rather than claiming transport acceptance.
    if (eventType !== 'ping') {
      this.dispatchLocalEvent('send_failed', { eventType, reason: 'unsupported' });
      return false;
    }

    if (!this.handshakeValidated) {
      errorManager.reportError(
        'Cannot send message: WebSocket handshake not validated yet.',
        ErrorSeverity.WARNING,
        'WebSocketService.send',
        { eventType }
      );
      return false;
    }

    try {
      // Preserve the wire field for compatibility; this UUID is not a sequence or authority.
      const sequenceId = typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
      const message = {
        type: eventType,
        data: data,
        sequenceId: sequenceId,
        timestamp: Date.now(), // Include client timestamp
      };

      this.socket.send(JSON.stringify(message));
      // Payloads and correlation labels are not diagnostic log data.
      return true;
    } catch (error) {
      this.dispatchLocalEvent('send_failed', { eventType, reason: 'send_error' });
      errorManager.reportError(
        `Failed to send WebSocket message [${eventType}]`,
        ErrorSeverity.ERROR,
        'WebSocketService.send',
        { error, eventType }
      );
      return false;
    }
  }

  /**
   * Retained for older callers, but the campaign transport does not accept voice
   * commands or binary frames. It reports the unsupported send and returns false.
   * @param {Uint8Array} binaryData - Legacy serialized voice data (never logged or queued).
   * @returns {boolean} Always false for the current campaign transport.
   */
  sendVoiceMessage(binaryData: Uint8Array): boolean {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      this.dispatchLocalEvent('send_failed', { eventType: 'voice.binary', reason: 'offline' });
      errorManager.reportError(
        'Cannot send voice message: WebSocket not connected or not open.',
        ErrorSeverity.WARNING,
        'WebSocketService.sendVoiceMessage',
        { readyState: this.socket?.readyState }
      );
      return false;
    }

    if (this.connectionStatus !== 'ready') {
      this.dispatchLocalEvent('send_failed', { eventType: 'voice.binary', reason: 'not_ready' });
      errorManager.reportError(
        'Cannot send voice message before campaign snapshot synchronization.',
        ErrorSeverity.WARNING,
        'WebSocketService.sendVoiceMessage',
        {}
      );
      return false;
    }

    this.dispatchLocalEvent('send_failed', { eventType: 'voice.binary', reason: 'unsupported' });
    return false;
  }

  /**
   * Subscribes a callback function to a specific event type received via WebSocket.
   * @param {string} eventType - The event type to subscribe to.
   * @param {(data: any) => void} callback - The function to call when the event is received.
   */
  subscribe(eventType: string, callback: (data: any) => void) {
    if (!this.eventHandlers.has(eventType)) {
      this.eventHandlers.set(eventType, new Set());
    }
    this.eventHandlers.get(eventType)?.add(callback);
    console.log(`Subscribed to WS event: ${eventType}`);
  }

  /**
   * Unsubscribes a callback function from a specific event type.
   * @param {string} eventType - The event type to unsubscribe from.
   * @param {(data: any) => void} callback - The specific callback function to remove.
   */
  unsubscribe(eventType: string, callback: (data: any) => void) {
    const handlers = this.eventHandlers.get(eventType);
    if (handlers) {
      handlers.delete(callback);
      console.log(`Unsubscribed from WS event: ${eventType}`);
      if (handlers.size === 0) {
        this.eventHandlers.delete(eventType);
      }
    }
  }

  /**
   * Handles the WebSocket 'open' event.
   */
  private isCurrentConnection(socket: WebSocket, generation: number): boolean {
    return this.socket === socket && this.connectionGeneration === generation;
  }

  /** Socket-open is transport-only; readiness waits for a validated server ack. */
  private handleOpen(socket: WebSocket, generation: number) {
    if (!this.isCurrentConnection(socket, generation)) return;
    this.isConnecting = false;
    this.setConnectionStatus('authenticating');
    void this.sendClientHandshake(socket, generation);
  }

  private handleClose(socket: WebSocket, generation: number, event: CloseEvent) {
    if (!this.isCurrentConnection(socket, generation)) return;
    this.isConnecting = false;
    this.socket = null;
    this.handshakeValidated = false;
    this.handshakeConfirmed = false;
    this.lastHandshake = null;
    this.dispatchLocalEvent('connection_closed', { code: event.code });
    if (!this.explicitlyClosed) this.attemptReconnect();
  }

  private handleError(socket: WebSocket, generation: number, _event: Event) {
    if (!this.isCurrentConnection(socket, generation)) return;
    this.handshakeValidated = false;
    this.handshakeConfirmed = false;
    this.lastHandshake = null;
    errorManager.reportError(
      'WebSocket error occurred.',
      ErrorSeverity.ERROR,
      'WebSocketService.handleError',
      {}
    );
    this.isConnecting = false;
    this.dispatchLocalEvent('connection_error', {});
  }

  /**
   * Handles incoming JSON messages from the WebSocket.
   */
  private handleMessage(event: MessageEvent) {
    try {
      const parsed = JSON.parse(event.data);

      if (!validateServerMessage(parsed)) {
        errorManager.reportError(
          'Received invalid WebSocket message structure.',
          ErrorSeverity.ERROR,
          'WebSocketService.handleMessage',
          {}
        );
        return;
      }

      const message = parsed;
      const messageType: string | undefined = message.type;

      if (messageType === 'handshake.ack') {
        this.handleHandshakeAck(message);
        return;
      }

      if (messageType === 'handshake.error') {
        this.handshakeValidated = false;
        this.handshakeConfirmed = false;
        this.lastHandshake = null;
        errorManager.reportError(
          'Server rejected WebSocket handshake.',
          ErrorSeverity.CRITICAL,
          'WebSocketService.handleMessage',
          {}
        );
        this.dispatchLocalEvent('handshake_failed', {});
        if (this.socket && this.socket.readyState === WebSocket.OPEN) {
          this.socket.close(4001, 'Handshake rejected by server');
        }
        return;
      }

      if (!this.handshakeValidated && messageType && !this.preHandshakeAllowed.has(messageType)) {
        console.warn(`Ignoring message [${messageType}] before handshake validation completes.`);
        return;
      }

      if (messageType === 'state.snapshot' || messageType === 'state.changed') {
        const envelope = message as unknown as RealtimeEnvelope;
        if (!this.isValidRealtimeEnvelope(envelope) || envelope.campaign_id !== this.campaignId) return;
        // The route owner decides whether this revision is new and requests a snapshot only for accepted invalidations.
        this.dispatchLocalEvent(messageType, envelope);
        return;
      }

      // Campaign realtime correctness uses durable server revisions and snapshots; legacy event sequence IDs are not authority.

      if (messageType && this.eventHandlers.has(messageType)) {
        // Dispatch to registered handlers
        this.eventHandlers.get(messageType)?.forEach(callback => {
          try {
            callback(message.data);
          } catch (callbackError) {
            console.error(`Error in WebSocket event handler for type [${messageType}]:`, callbackError);
          }
        });
      } else {
        console.log(`No handler registered for WS event type: ${messageType}`);
      }
    } catch {
      errorManager.reportError(
        'Failed to parse or handle incoming WebSocket message.',
        ErrorSeverity.ERROR,
        'WebSocketService.handleMessage',
        {}
      );
    }
  }

  private isValidRealtimeEnvelope(value: RealtimeEnvelope): boolean {
    return value.protocol_version === '1.0.0' && typeof value.event_id === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value.event_id) && typeof value.campaign_id === 'string' && (value.session_id === null || typeof value.session_id === 'string') && Number.isSafeInteger(value.server_revision) && value.server_revision >= 0 && typeof value.data === 'object' && value.data !== null;
  }

  /**
   * Handles incoming binary protobuf voice messages from the WebSocket.
   * Voice messages use protobuf for bandwidth efficiency (73% reduction vs JSON).
   */
  private async handleBinaryMessage(event: MessageEvent, socket: WebSocket, generation: number) {
    if (!this.handshakeValidated || !this.isCurrentConnection(socket, generation)) return;
    try {
      let binaryData: ArrayBuffer;
      if (event.data instanceof Blob) {
        binaryData = await event.data.arrayBuffer();
      } else if (event.data instanceof ArrayBuffer) {
        binaryData = event.data;
      } else if (event.data instanceof Uint8Array) {
        binaryData = event.data.buffer.slice(event.data.byteOffset, event.data.byteOffset + event.data.byteLength) as ArrayBuffer;
      } else {
        console.warn('Received unsupported binary payload type', { type: typeof event.data });
        return;
      }

      if (!this.handshakeValidated || !this.isCurrentConnection(socket, generation)) return;
      const bytes = new Uint8Array(binaryData);

      let universal: mythbound.websocket.v1.UniversalMessage;
      try {
        universal = mythbound.websocket.v1.UniversalMessage.decode(bytes);
      } catch (decodeError) {
        this.dispatchVoiceEvent('voice_message_binary', { binaryData: bytes });
        throw decodeError;
      }

      this.dispatchVoiceEvent('voice_message_binary', {
        binaryData: bytes,
        universalMessage: universal,
      });

      if (universal.voiceData) {
        const voiceData = universal.voiceData;
        const audioBytes = voiceData.audioData ?? new Uint8Array();
        const config = voiceData.audioConfig ?? {};

        this.dispatchVoiceEvent('voice_data', {
          rawPCM: audioBytes,
          metadata: {
            sessionId: voiceData.sessionId,
            participantId: voiceData.participantId,
            audioFormat: voiceData.audioFormat ?? 'pcm',
            sampleRate: config.sampleRate ?? 48000,
            channels: config.channels ?? 1,
            bitDepth: config.bitDepth ?? 16,
            frameSize: config.frameSize ?? 4096,
            durationMs: voiceData.durationMs ?? null,
          },
          universalMessage: universal,
        });
        return;
      }

      if (universal.voiceJoin) {
        const voiceJoin = universal.voiceJoin;
        this.dispatchVoiceEvent('voice_join', {
          characterId: voiceJoin.characterId,
          characterName: voiceJoin.characterName,
          discordUserId: voiceJoin.discordUserId,
          oauthProvider: voiceJoin.oauthProvider,
          audioConfig: voiceJoin.audioConfig,
          universalMessage: universal,
        });
        return;
      }

      if (universal.voiceLeave) {
        const voiceLeave = universal.voiceLeave;
        this.dispatchVoiceEvent('voice_leave', {
          participantId: voiceLeave.participantId,
          leaveReason: voiceLeave.leaveReason,
          universalMessage: universal,
        });
        return;
      }

      if (universal.voiceActivity) {
        const activity = universal.voiceActivity;
        this.dispatchVoiceEvent('voice_activity', {
          sessionId: activity.sessionId,
          participantId: activity.participantId,
          isSpeaking: activity.isSpeaking,
          energyLevel: activity.energyLevel,
          durationMs: activity.durationMs,
          universalMessage: universal,
        });
        return;
      }

      if (universal.voiceModeChange) {
        const modeChange = universal.voiceModeChange;
        this.dispatchVoiceEvent('voice_mode_change', {
          mode: modeChange.mode,
          previousMode: modeChange.previousMode,
          wakeWordEnabled: modeChange.wakeWordEnabled,
          wakePhrase: modeChange.wakePhrase,
          broadcast: modeChange.broadcast,
          universalMessage: universal,
        });
        return;
      }

      if (universal.voiceFeedback) {
        const feedback = universal.voiceFeedback;
        this.dispatchVoiceEvent('voice_feedback', {
          state: feedback.state,
          emoji: feedback.emoji,
          participantId: feedback.participantId,
          universalMessage: universal,
        });
        return;
      }

      console.debug('Received binary message with no mapped handler', universal);

    } catch (error) {
      errorManager.reportError(
        'Failed to handle incoming binary voice message.',
        ErrorSeverity.ERROR,
        'WebSocketService.handleBinaryMessage',
        { error, dataLength: event.data?.byteLength || 0 }
      );
    }
  }

  private dispatchVoiceEvent(eventType: string, payload: any) {
    const handlers = this.eventHandlers.get(eventType);
    if (!handlers || handlers.size === 0) {
      return;
    }

    handlers.forEach(callback => {
      try {
        callback(payload);
      } catch (callbackError) {
        console.error(`Error in voice event handler for ${eventType}:`, callbackError);
      }
    });
  }

  /**
   * Dispatches an event locally to subscribed handlers (e.g., for connection status).
   */

  /**
   * Sends client-initiated handshake immediately after connection opens.
   */
  /**
   * Sends protocol metadata and an optional bearer credential. The bearer is
   * transport material, not an identity assertion; only server validation
   * through a compatible acknowledgement unlocks application traffic.
   */
  private async sendClientHandshake(socket: WebSocket, generation: number) {
    if (!this.isCurrentConnection(socket, generation) || socket.readyState !== WebSocket.OPEN) return;

    let accessToken: string | undefined;
    try {
      const headers = await buildAuthHeaders();
      const authorization = headers.Authorization ?? headers.authorization;
      if (authorization?.startsWith('Bearer ')) accessToken = authorization.slice('Bearer '.length);
    } catch {
      // Cookie-only sessions remain supported when local bearer storage is unavailable.
    }

    if (!this.isCurrentConnection(socket, generation) || socket.readyState !== WebSocket.OPEN) return;
    // Header lookup is asynchronous; the current-socket check immediately above
    // prevents a late credential from being sent after disconnect or reconnect.
    const data: Record<string, unknown> = {
      protocol_version: this.expectedProtocolVersion,
      contract_versions: { ...this.expectedContractVersions },
      serializers: {
        preferred: this.preferredSerializer,
        supported: ['json', 'protobuf-voice'],
      },
      client_info: {
        client_type: this.clientIdentifier,
        client_version: this.clientVersion,
      },
      realtime: { version: '1.0.0' },
    };
    if (accessToken) data.access_token = accessToken;

    try {
      socket.send(JSON.stringify({ type: 'handshake', data }));
    } catch {
      errorManager.reportError(
        'Failed to send client handshake.',
        ErrorSeverity.ERROR,
        'WebSocketService.sendClientHandshake',
        {}
      );
    }
  }

  /**
   * Handles handshake acknowledgment from server.
   */
  private handleHandshakeAck(message: any) {
    const data = message.data || message;
    
    // Validate protocol and contract versions
    const issues: string[] = [];
    
    if (!data.protocol_version || data.protocol_version !== this.expectedProtocolVersion) {
      issues.push('Protocol version mismatch.');
    }
    
    if (!data.contract_versions) {
      issues.push('Missing contract versions in handshake acknowledgment');
    } else {
      if (data.contract_versions.websocket !== this.expectedContractVersions.websocket) {
        issues.push('WebSocket contract version mismatch.');
      }
      if (data.contract_versions.protobuf_schema !== this.expectedContractVersions.protobuf_schema) {
        issues.push('Protobuf schema version mismatch.');
      }
    }

    if (data.realtime?.version !== '1.0.0') issues.push('Realtime version mismatch.');

    if (issues.length > 0) {
      errorManager.reportError(
        'Handshake acknowledgment validation failed.',
        ErrorSeverity.CRITICAL,
        'WebSocketService.handleHandshakeAck',
        { issues }
      );
      this.handshakeValidated = false;
      this.handshakeConfirmed = false;
      this.lastHandshake = null;
      this.dispatchLocalEvent('handshake_failed', { issues });
      if (this.socket && this.socket.readyState === WebSocket.OPEN) {
        this.socket.close(4000, 'Handshake validation failed');
      }
      return;
    }

    // Transport-open is not readiness: only a compatible server ack unlocks
    // application messages and emits the ready event.
    this.handshakeValidated = true;
    this.handshakeConfirmed = true;
    this.lastHandshake = data;
    this.dispatchLocalEvent('handshake_validated', { handshake: data });
    if (this.lifecycleTimer) clearTimeout(this.lifecycleTimer);
    this.lifecycleTimer = null;
    this.setConnectionStatus('synchronizing');
    this.requestStateRefresh();
  }

  private dispatchLocalEvent(eventType: string, data: any) {
     if (this.eventHandlers.has(eventType)) {
        this.eventHandlers.get(eventType)?.forEach(callback => {
          try {
            callback(data);
          } catch (callbackError) {
            console.error(`Error in local event handler for type [${eventType}]:`, callbackError);
          }
        });
      }
  }

  /**
   * Attempts to reconnect to the WebSocket using exponential backoff.
   */
  private attemptReconnect() {
    if (this.isConnecting || this.explicitlyClosed) {
        return; // Don't attempt reconnect if already connecting or explicitly closed
    }

    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('Maximum WebSocket reconnection attempts reached. Stopping.');
      // Dispatch a local event indicating permanent failure
       this.dispatchLocalEvent('connection_failed', { attempts: this.reconnectAttempts });
      this.setConnectionStatus('reconnect_exhausted');
      return;
    }

    this.reconnectAttempts++;
    // Revoke ready immediately while waiting: callers must not treat an open
    // interval during backoff as synchronized or use it for consequential sends.
    this.setConnectionStatus('retrying');
    const delay = this.reconnectDelay * Math.pow(this.reconnectBackoffFactor, this.reconnectAttempts - 1);
    console.log(`Attempting WebSocket reconnect in ${Math.round(delay)}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})...`);
    
    // Use a retained timer so campaign/logout changes can cancel retries.
    this.reconnectTimer = setTimeout(() => {
        if (!this.explicitlyClosed) { // Double-check it wasn't closed during the timeout
            this.connect();
        }
    }, delay);
  }
  
  /**
   * Gets the current readiness state of the WebSocket connection.
   * @returns {WebSocket['readyState'] | undefined} The readyState or undefined if socket is null.
   */
  getReadyState(): WebSocket['readyState'] | undefined {
      return this.socket?.readyState;
  }

  /**
   * Alias for subscribe() - provides on() method for event handler registration
   * @param {string} eventType - The event type to subscribe to.
   * @param {(data: any) => void} callback - The function to call when the event is received.
   */
  on(eventType: string, callback: (data: any) => void) {
    this.subscribe(eventType, callback);
  }

  /**
   * Alias for unsubscribe() - provides off() method for event handler removal
   * @param {string} eventType - The event type to unsubscribe from.
   * @param {(data: any) => void} callback - The specific callback function to remove.
   */
  off(eventType: string, callback: (data: any) => void) {
    this.unsubscribe(eventType, callback);
  }
}

// Export a singleton instance of the service
export const webSocketService = new WebSocketService();
